import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, Output, Signal, WritableSignal, computed } from "@angular/core";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { InputChangeEvent, InputConfig } from "@ez-library/types/ez-form.types";
import { ColDef, GridOptions } from "ag-grid-community";
import { TerminologyIdentityDirective } from "@app/shared/terminology/terminology-identity.directive";
import { TerminologyFormIdentityDirective } from "@app/shared/terminology/terminology-form-identity.directive";
import { createTerminologyUsageSignals } from "@app/shared/terminology/terminology-usage-signals";
import { createTerminologyGridHeader } from "@app/shared/terminology/terminology-grid-header";
import { HEARING_ASSESSMENT_TERMINOLOGY } from "./hearing-assessment.terminology";
import { AtHearingFormValues, HearingRecordOffense } from "../hearing-record.models";

export interface HearingTopicGroup {
  title: string;
  description?: string;
  config: InputConfig[];
  testId: string;
}

/** Recorded source violations followed by hearing-scoped panel observations. */
@Component({
  selector: "phms-hearing-topic-section",
  standalone: true,
  imports: [CommonModule, UiLibraryModule, TerminologyIdentityDirective, TerminologyFormIdentityDirective],
  templateUrl: "./hearing-topic-section.component.html",
  styleUrl: "./hearing-topic-section.component.scss",
})
export class HearingTopicSectionComponent {
  @Input() testId = "";
  @Input() groups: readonly HearingTopicGroup[] = [];
  private violationRows: HearingRecordOffense[] | null = null;
  @Input() set violations(value: readonly HearingRecordOffense[] | null) {
    this.violationRows = value === null ? null : [...value];
  }
  get violations(): HearingRecordOffense[] | null { return this.violationRows; }
  @Input() values!: WritableSignal<AtHearingFormValues>;
  @Input() canEdit!: Signal<boolean>;
  @Output() inputChange = new EventEmitter<InputChangeEvent>();
  readonly terminologyUsage = HEARING_ASSESSMENT_TERMINOLOGY;
  readonly text = createTerminologyUsageSignals(this.terminologyUsage);
  readonly formUsage = { institutionalSummary: this.terminologyUsage.behaviorSummary };
  private readonly statusHeader = createTerminologyGridHeader(this.terminologyUsage.status, this.text.status);
  readonly columnDefaults: ColDef<HearingRecordOffense> = {
    flex: 1, minWidth: 110, editable: false, sortable: false, filter: false,
    resizable: true, wrapText: true, autoHeight: true, wrapHeaderText: true, autoHeaderHeight: true,
  };
  readonly gridOptions: GridOptions<HearingRecordOffense> = { pagination: false, defaultColDef: this.columnDefaults };
  readonly columns = computed<ColDef<HearingRecordOffense>[]>(() => [
    { field: "eventDate", headerName: "Date", valueFormatter: ({ value }) => this.formatDate(value) },
    { field: "summary", headerName: "Description", flex: 3, minWidth: 220, valueFormatter: ({ value }) => value || "\u2014" },
    { field: "statuteReference", headerName: "Reference", minWidth: 130, valueFormatter: ({ value }) => value || "\u2014" },
    { field: "dispositionCode", ...this.statusHeader(), valueFormatter: ({ value }) => value || "\u2014" },
  ]);

  onInputChange(event: InputChangeEvent): void {
    if (!this.canEdit() || !this.groups.some(group => group.config.some(field => field.name === event.name))) return;
    this.values.update(current => ({ ...current, [event.name]: event.value ?? "" }));
    this.inputChange.emit(event);
  }

  formatDate(value: string | null | undefined): string {
    // Date-only source records represent a calendar day, not midnight UTC.
    const date = value ? new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value) : null;
    return !date || Number.isNaN(date.getTime()) ? "\u2014" : new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", year: "numeric" }).format(date);
  }
}
